import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Key, Mail, ShoppingCart, Wallet, Globe, AlertCircle, Inbox } from "lucide-react";
import { Link } from "react-router-dom";

const Stat = ({ label, value, sub, icon: Icon, accent, testId }) => (
  <div className="border border-border bg-card p-6 rounded-sm" data-testid={testId}>
    <div className="flex items-center justify-between mb-4">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
      <Icon className={`h-4 w-4 ${accent || "text-muted-foreground"}`} />
    </div>
    <p className="text-4xl font-black tracking-tight tabular-nums">{value}</p>
    {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
  </div>
);

const STATUS_BADGE = {
  active: "text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300",
  blocked: "text-amber-700 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300",
  revoked: "text-rose-700 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-300",
  pending: "text-sky-700 bg-sky-50 dark:bg-sky-950/40 dark:text-sky-300",
};

const PAY_BADGE = {
  paid: "text-emerald-700 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-300",
  initiated: "text-sky-700 bg-sky-50 dark:bg-sky-950/40 dark:text-sky-300",
  failed: "text-rose-700 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-300",
  expired: "text-amber-700 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-300",
};

export default function Dashboard() {
  const [licenses, setLicenses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [pricing, setPricing] = useState({ plan_price: 69, plan_currency: "€" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/admin/licenses").then(({ data }) => setLicenses(data)),
      api.get("/admin/contact").then(({ data }) => setContacts(data)),
      api.get("/admin/payments").then(({ data }) => setPayments(data)),
      api.get("/admin/pricing").then(({ data }) => setPricing(data)),
    ])
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  // Metrics
  const activeLicenses = licenses.filter((l) => l.status === "active").length;
  const lockedLicenses = licenses.filter((l) => l.domain).length;
  const unreadContacts = contacts.filter((c) => !c.read).length;

  const paid = payments.filter((p) => p.payment_status === "paid");
  const revenue = paid.reduce((acc, p) => acc + (p.amount || 0), 0);

  // Revenu du mois en cours
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
  const monthPaid = paid.filter((p) => p.created_at >= monthStart);
  const monthRevenue = monthPaid.reduce((acc, p) => acc + (p.amount || 0), 0);

  const recentLicenses = licenses.slice(0, 5);
  const recentPayments = payments.slice(0, 5);
  const recentContacts = contacts.slice(0, 5);

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">// WiLink commercial</p>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Vue d'ensemble</h1>
        <p className="text-sm text-muted-foreground mt-1">Activité commerciale du produit WiLink.</p>
      </div>

      {error && <p className="text-destructive text-sm">{error}</p>}

      {!loading && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat
              label="Revenu total"
              value={`${revenue.toFixed(0)}${pricing.plan_currency}`}
              sub={`${paid.length} ventes`}
              icon={Wallet}
              accent="text-emerald-500"
              testId="stat-revenue"
            />
            <Stat
              label={`Ventes (${now.toLocaleString("fr-FR", { month: "short" })})`}
              value={`${monthRevenue.toFixed(0)}${pricing.plan_currency}`}
              sub={`${monthPaid.length} ce mois-ci`}
              icon={ShoppingCart}
              accent="text-primary"
              testId="stat-month-revenue"
            />
            <Stat
              label="Licences actives"
              value={activeLicenses}
              sub={`${lockedLicenses} verrouillées sur domaine`}
              icon={Key}
              testId="stat-licenses"
            />
            <Stat
              label="Messages non lus"
              value={unreadContacts}
              sub={`${contacts.length} au total`}
              icon={Mail}
              accent={unreadContacts > 0 ? "text-primary" : undefined}
              testId="stat-unread-contacts"
            />
          </div>

          {/* No data yet */}
          {licenses.length === 0 && contacts.length === 0 && payments.length === 0 && (
            <section className="border border-dashed border-border rounded-sm p-12 text-center">
              <AlertCircle className="h-10 w-10 mx-auto text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">
                Pas encore d'activité commerciale. Configurez Stripe en LIVE pour commencer à recevoir des ventes.
              </p>
            </section>
          )}

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Recent licenses */}
            <section className="border border-border bg-card rounded-sm">
              <header className="flex items-center justify-between p-5 border-b border-border">
                <div className="flex items-center gap-2">
                  <Key className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-sm font-bold tracking-tight uppercase letter-spacing-wider">Licences récentes</h2>
                </div>
                <Link to="/admin/licenses" className="text-xs text-primary hover:underline">Voir tout →</Link>
              </header>
              {recentLicenses.length === 0 ? (
                <p className="p-8 text-center text-sm text-muted-foreground">Aucune licence pour le moment.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {recentLicenses.map((lic) => (
                    <li key={lic.id} className="p-4 flex items-center gap-3" data-testid={`recent-lic-${lic.id}`}>
                      <div className="flex-1 min-w-0">
                        <code className="text-xs font-mono">{lic.key}</code>
                        <p className="text-xs text-muted-foreground truncate">
                          {lic.email}
                          {lic.domain && (
                            <span className="inline-flex items-center gap-1 ml-2">
                              · <Globe className="h-3 w-3" /> {lic.domain}
                            </span>
                          )}
                        </p>
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_BADGE[lic.status] || ""}`}>
                        {lic.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {/* Recent payments */}
            <section className="border border-border bg-card rounded-sm">
              <header className="flex items-center justify-between p-5 border-b border-border">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-sm font-bold tracking-tight uppercase">Paiements récents</h2>
                </div>
                <Link to="/admin/commerce" className="text-xs text-primary hover:underline">Voir tout →</Link>
              </header>
              {recentPayments.length === 0 ? (
                <p className="p-8 text-center text-sm text-muted-foreground">Aucun paiement.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {recentPayments.map((p) => (
                    <li key={p.id} className="p-4 flex items-center gap-3" data-testid={`recent-pay-${p.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{p.name || p.email || "Anonyme"}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(p.created_at).toLocaleString("fr-FR")} · {p.amount} {p.currency?.toUpperCase()}
                        </p>
                      </div>
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${PAY_BADGE[p.payment_status] || "bg-muted text-muted-foreground"}`}>
                        {p.payment_status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          {/* Recent contacts */}
          <section className="border border-border bg-card rounded-sm">
            <header className="flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center gap-2">
                <Inbox className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-sm font-bold tracking-tight uppercase">Derniers messages</h2>
              </div>
              <Link to="/admin/contact" className="text-xs text-primary hover:underline">Voir tout →</Link>
            </header>
            {recentContacts.length === 0 ? (
              <p className="p-8 text-center text-sm text-muted-foreground">Aucun message.</p>
            ) : (
              <ul className="divide-y divide-border">
                {recentContacts.map((c) => (
                  <li key={c.id} className="p-4 flex items-center gap-3" data-testid={`recent-contact-${c.id}`}>
                    {!c.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0" title="Non lu" />}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${!c.read ? "font-bold" : "font-medium"}`}>{c.name} — <span className="text-muted-foreground font-normal">{c.email}</span></p>
                      <p className="text-xs text-muted-foreground truncate">{c.message}</p>
                    </div>
                    <span className="text-[10px] text-muted-foreground shrink-0">
                      {new Date(c.created_at).toLocaleDateString("fr-FR")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
